import React, { useState, useMemo } from 'react';
import { Popover } from './Popover';
import { Avatar } from './Avatar';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

export function UserSelector({ 
    users = [], 
    selectedId, 
    onChange, 
    placeholder = 'Select a user...',
    className = '' 
}) {
    const [searchQuery, setSearchQuery] = useState('');

    const selectedUser = useMemo(() => {
        return users.find(u => u.id === selectedId);
    }, [users, selectedId]);

    const filteredUsers = useMemo(() => {
        if (!searchQuery) return users;
        return users.filter(u => 
            u.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            u.email?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [users, searchQuery]);

    const [isOpen, setIsOpen] = useState(false);

    const trigger = (
        <div className={`
            h-10 px-3 rounded-lg border border-[var(--border)]
            bg-[var(--bg-surface)] hover:border-[var(--border-strong)] transition-colors
            flex items-center gap-2 min-w-[200px]
            ${className}
        `}>
            {selectedUser ? (
                <>
                    <Avatar 
                        src={selectedUser.avatar} 
                        initials={selectedUser.name} 
                        size="xs" 
                    />
                    <span className="text-sm font-medium text-[var(--text-primary)] truncate flex-1 text-left">
                        {selectedUser.name}
                    </span>
                </>
            ) : (
                <span className="text-sm text-[var(--text-tertiary)] flex-1 text-left">
                    {placeholder}
                </span>
            )}
            <div className={`flex items-center justify-center text-[var(--text-tertiary)] opacity-70 transition-transform duration-200 origin-center ${isOpen ? 'rotate-180' : ''}`}>
                <ObliqueIcon name="expand_more" size={18} />
            </div>
        </div>
    );

    const content = (
        <div className="flex flex-col w-[260px] max-h-[320px]">
            <div className="p-2 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-surface)] z-10 rounded-t-2xl">
                <div className="relative flex items-center">
                    <ObliqueIcon name="search" size={16} className="absolute left-3 text-[var(--text-tertiary)] pointer-events-none" />
                    <input 
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-[var(--bg-muted)] border-none rounded-md py-1.5 pl-9 pr-3 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                    />
                </div>
            </div>
            
            <div className="flex flex-col overflow-y-auto p-1 custom-scrollbar flex-1">
                {filteredUsers.length === 0 ? (
                    <div className="py-6 text-center text-sm text-[var(--text-tertiary)]">
                        No users found
                    </div>
                ) : (
                    filteredUsers.map(user => {
                        const isSelected = user.id === selectedId;
                        return (
                            <div 
                                key={user.id}
                                onClick={() => {
                                    onChange?.(user.id);
                                    setIsOpen(false);
                                    setSearchQuery('');
                                }}
                                className={`
                                    flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors
                                    ${isSelected ? 'bg-[var(--bg-subtle)]' : 'hover:bg-[var(--bg-muted)]'}
                                `}
                            >
                                <Avatar src={user.avatar} initials={user.name} size="sm" status={user.status} />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                                        {user.name}
                                    </span>
                                    {user.email && (
                                        <span className="text-xs text-[var(--text-tertiary)] truncate">
                                            {user.email}
                                        </span>
                                    )}
                                </div>
                                {isSelected && (
                                    <div className="ml-auto text-[var(--accent)]">
                                        <ObliqueIcon name="check" size={16} />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <Popover 
            trigger={trigger} 
            content={content} 
            position="bottom" 
            isOpen={isOpen}
            onOpenChange={setIsOpen}
        />
    );
}
